// src/hooks/useOrganoidData.js

import { parseEnvironmentalData } from './parseEnvironmentalData';
import { parseSpikeData } from './parseSpikeData';
import { useEffect, useRef, useState } from 'react';
import { authFetch } from '../utils/authFetch';

export function useOrganoidData(
  selectedOrganoid, 
  isRecording, 
  setDataBuffer, 
  setSpikeBuffer, 
  samplingInterval,
  uiState,
  setUiState,
  setFinalRecoveryFailed,
) {
  const lastChunkIDRef = useRef(-1); // starts at -1 so the first request is chunkID 0
  const experimentStartTimeRef = useRef(null); // persist across polls
  const intervalRef = useRef(null); // persistent interval ID

  const [isSyncing, setIsSyncing] = useState(false); // do i insert this here???
  const isSyncingRef = useRef(false); // 🔒 internal guard for polling

  useEffect(() => {
    if (!selectedOrganoid || !samplingInterval || uiState !== 'running') return;    

    // Helper to recover missed chunks if polling resumes after suspension
    const fetchMissingChunks = async (chunkIDs) => {
      const uniqueChunks = [...new Set(chunkIDs)].filter(id => id > lastChunkIDRef.current);
      if (uniqueChunks.length === 0) {
        console.log(`[RECOVERY] No new chunks to recover — skipping`);
        return;
      }

      console.log(`[FETCH_MISSING_CHUNK][frontend] Starting recovery for chunks:`, chunkIDs); 

      isSyncingRef.current = true;
      setIsSyncing(true); // 🔘 Start syncing overlay

      let highestRecovered = lastChunkIDRef.current;
      
      for (const chunkID of uniqueChunks) {
        if (chunkID <= lastChunkIDRef.current) {
          console.log(`[RECOVERY] Skipping stale chunk ${chunkID}`);
          continue;
        }

        console.log(`[FETCH_MISSING_CHUNK][frontend] Attempting chunk ${chunkID}`);
        try {
          const res = await authFetch(`${process.env.REACT_APP_API_URL}/data/${selectedOrganoid}?chunk_id=${chunkID}`);
          const json = await res.json();

          if (json.data?.length) {
            const experimentStartTime = experimentStartTimeRef.current;
            const timeAlignedData = json.data.map(entry => ({
              ...entry,
              relativeTime: entry.timestamp - experimentStartTime
            }));

            parseEnvironmentalData({ ...json, data: timeAlignedData }, setDataBuffer);
            parseSpikeData({ ...json, data: timeAlignedData }, setSpikeBuffer);

            lastChunkIDRef.current = chunkID; // optimistic advance
            highestRecovered = Math.max(highestRecovered, chunkID);
            console.log(`[RECOVERY] Fetched missing chunk ${chunkID}`);
          }
        } catch (err) {
          console.error(`[RECOVERY] Failed to fetch chunk ${chunkID}:`, err);
        }
      }

      lastChunkIDRef.current = highestRecovered; // ✅ advance once after recovery

      // 🔍 Final catch-up check
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/data/${selectedOrganoid}?chunk_id=${highestRecovered + 1}`);
        const json = await res.json();

        if (json.chunkID === highestRecovered + 1 && json.data?.length) {
          console.log(`[RECOVERY] New chunk ${json.chunkID} appeared during recovery — continuing recovery`);
          setTimeout(() => {
            fetchMissingChunks([json.chunkID]); // recursive recovery
          }, 100); // 100ms delay
        } else {
          console.log(`[RECOVERY] Recovery complete — caught up to chunk ${highestRecovered}`);
          isSyncingRef.current = false;
          setIsSyncing(false);
        }

        if (!json.data || json.data.length === 0) {
          console.log(`[RECOVERY] No new data — assuming experiment is complete`);
          isSyncingRef.current = false;
          setIsSyncing(false);
          return;
        }

      } catch (err) {
        console.error(`[RECOVERY] Final catch-up check failed:`, err);

        isSyncingRef.current = false;
        setIsSyncing(false); // 🔘 End syncing overlay
        setFinalRecoveryFailed(true); // 🔘 mark recovery failure
        setUiState('complete'); // state is advanced so that completion message shows
      }
    };

    // Poll organoid data
    const pollData = () => {
      if (isSyncingRef.current) {
        console.log("[POLL] Skipping poll — recovery in progress");
        return;
      }
      
      const nextChunkID = lastChunkIDRef.current + 1;
      //lastChunkIDRef.current = nextChunkID; // optimistic advance

      console.log("Polling status for:", selectedOrganoid);

      authFetch(`${process.env.REACT_APP_API_URL}/data/${selectedOrganoid}?chunk_id=${nextChunkID}`)
        .then(res => res.json())
        .then(json => {

            if (
              !experimentStartTimeRef.current &&
              json.data &&
              json.data.length > 0 &&
              json.data[0].experimentStartTime
            ) {
              experimentStartTimeRef.current = json.data[0].experimentStartTime;
            }

            const experimentStartTime = experimentStartTimeRef.current;
            if (!experimentStartTime) {
              console.warn("experimentStartTime not yet initialized — skipping relativeTime computation");
              return;
            }

            console.log("Raw data from backend:", json.data);
            //if (!json.data || json.data.length === 0) return;
            if (!json.data || json.data.length === 0) {
              console.log("No data returned for chunk", nextChunkID);

              // Fallback recovery check
              authFetch(`${process.env.REACT_APP_API_URL}/data/${selectedOrganoid}?chunk_id=${nextChunkID + 1}`)
                .then(res => res.json())
                .then(nextJson => {
                  if (nextJson.chunkID === nextChunkID + 1 && nextJson.data?.length) {
                    console.log(`[RECOVERY] Detected new chunk ${nextJson.chunkID} after empty poll — triggering recovery`);
                    fetchMissingChunks([nextJson.chunkID]);
                  }
                })
                .catch(err => {
                  console.error(`[RECOVERY] Fallback check failed:`, err);
                });

              return;
            }

            if (json.chunkID !== nextChunkID) {
              console.warn("Chunk mismatch:", json.chunkID, "expected:", nextChunkID);

              if (json.chunkID > nextChunkID) {
                const missingChunks = [];
                for (let i = nextChunkID; i < json.chunkID; i++) {
                  missingChunks.push(i);
                }
                fetchMissingChunks(missingChunks);
              }

              return;
            }
            
            if (json.chunkID < nextChunkID) {
              console.warn(`[FETCH_MISSING_CHUNK][frontend] Received stale chunk ${json.chunkID}, expected ${nextChunkID}`);
              return;
            }

            if (json.latestChunkID > nextChunkID) {
              const missingChunks = [];
              for (let i = nextChunkID; i < json.latestChunkID; i++) {
                missingChunks.push(i);
              }
              fetchMissingChunks(missingChunks);
            }

            console.log("Received chunk:", json.chunkID);
            lastChunkIDRef.current = json.chunkID

            const timeAlignedData = json.data.map(entry => ({
              ...entry,
              relativeTime: entry.timestamp - experimentStartTime
            }));

            console.log("Time-aligned entry:", timeAlignedData[0]);

            parseEnvironmentalData({ ...json, data: timeAlignedData }, setDataBuffer);
            parseSpikeData({ ...json, data: timeAlignedData }, setSpikeBuffer);

        })
        .catch(err => {
          console.error('Error fetching data:', err);
          // ✅ Trigger recovery failure if backend is unreachable
          if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
            console.warn('[RECOVERY] Backend unreachable — marking finalRecoveryFailed');
            setFinalRecoveryFailed(true);
            setUiState('complete'); // state is advanced so that completion message shows
          }

        });
    }

    const startPolling = () => {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(pollData, samplingInterval);
      }
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log("[VISIBILITY] Tab is visible — resuming polling");

        // ✅ Insert heartbeat check here
        authFetch(`${process.env.REACT_APP_API_URL}/heartbeat`)
          .then(res => {
            if (!res.ok) throw new Error("Backend unreachable");
            startPolling();  
          })
          .catch(err => {
            console.warn("[RECOVERY] Heartbeat failed — backend unreachable");
            setFinalRecoveryFailed(true);
            setUiState('complete'); // state is advanced so that completion message shows
          });

        //startPolling();

        // Final recovery sweep if recording has ended
        if (!isRecording) {
          console.log("[RECOVERY] Tab became visible after recording ended — checking for final chunks");
          authFetch(`${process.env.REACT_APP_API_URL}/data/${selectedOrganoid}?chunk_id=${lastChunkIDRef.current + 1}`)
            .then(res => res.json())
            .then(json => {
              if (json.chunkID > lastChunkIDRef.current && json.data?.length) {
                const missingChunks = [];
                for (let i = lastChunkIDRef.current + 1; i <= json.chunkID; i++) {
                  missingChunks.push(i);
                }
                fetchMissingChunks(missingChunks);
              }
            })
            .catch(err => {
              console.error("[RECOVERY] Final sweep after tab visibility failed:", err);
            });
        }

      } else {
        console.log("[VISIBILITY] Tab is hidden — stopping polling (desktop test mode)");
        stopPolling(); // optional: keep polling if you want uninterrupted accumulation
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    startPolling();

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedOrganoid, isRecording, samplingInterval, uiState]);

  function resetDataState() {
    lastChunkIDRef.current = -1;
    experimentStartTimeRef.current = null;

    isSyncingRef.current = false;
    setIsSyncing(false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setDataBuffer([]);
    setSpikeBuffer([]);
  }

  return { isSyncing, resetDataState }; // 👈 expose syncing state

}